import { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import L, {
  Draw,
  type LayerEvent,
  type LeafletKeyboardEvent,
  type DrawEvents,
  type LeafletEventHandlerFn,
  Polygon,
  LatLngLiteral,
  LatLng,
  DivIcon,
  Point,
  drawLocal,
  Edit,
  DrawMap,
} from 'leaflet'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate } from 'react-router'
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js'
import type { PolylineType, PolygonType, ExtendedLayer } from './types'
import useBuildQueryString from '../../../../../shared/hooks/useBuildQueryString'
import useParam from '../../../../../shared/hooks/useParam'
import { polygonParam, polylineParam, type PolyDrawing } from '../../../query-params'
import useMapCenterToMarker from '../../../../../shared/hooks/useMapCenterToMarker'
import { useMapContext } from '../../../../../shared/contexts/map/MapContext'
import { routing } from '../../../../../routes'
import { pageParam } from '../../../../SearchPage/query-params'
import { isAuthenticated } from '../../../../../shared/utils/auth/auth'

export const DRAWN_ITEM_CLASS = 'drawtool-drawn-item'

const drawIcon = new DivIcon({
  iconSize: new Point(15, 15),
  className: 'leaflet-div-icon leaflet-editing-icon ',
})

const { tooltip: tooltipPolygon } = drawLocal.draw.handlers.polygon
const { tooltip: tooltipPolyline } = drawLocal.draw.handlers.polyline

tooltipPolygon.start = 'Klik op de kaart om te beginnen'
tooltipPolygon.cont = 'Klik op de kaart om een punt toe te voegen'
tooltipPolygon.end = 'Klik op de kaart om verder te gaan of klik op de eerste punt om te eindigen'

tooltipPolyline.start = 'Klik op de kaart om te meten'
tooltipPolyline.cont = 'Klik op de kaart om een lijn te maken'
tooltipPolyline.end = 'Klik op de kaart om verder te gaan of klik op de laatste punt om te eindigen'

Edit.PolyVerticesEdit = Edit.PolyVerticesEdit.extend({
  options: {
    icon: drawIcon,
  },
})

export const getTotalDistance = (
  latLngs: Array<Pick<LatLng, 'lat' | 'lng' | 'distanceTo'>>,
  isPolygon: boolean,
) => {
  return latLngs.reduce(
    (total, latlng, i) => {
      if (i > 0) {
        const dist = latlng.distanceTo(latLngs[i - 1])
        return total + dist
      }
      return total
    },
    isPolygon ? latLngs[0].distanceTo(latLngs[latLngs.length - 1]) : 0,
  )
}

const getLayerCoordinates = (layer: ExtendedLayer) => layer.getLatLngs().flat(2)

const getDistanceLabel = (layer: ExtendedLayer) => {
  const latLngs = getLayerCoordinates(layer)
  const distance = getTotalDistance(latLngs, layer instanceof Polygon)
  const label =
    distance >= 1000
      ? `${L.GeometryUtil.formattedNumber(`${distance / 1000}`, 2)} km`
      : `${L.GeometryUtil.formattedNumber(`${distance}`, 2)} m`

  if (layer instanceof Polygon) {
    return `${L.GeometryUtil.readableArea(L.GeometryUtil.geodesicArea(latLngs), true, {
      m: 1,
    })}, ${label}`
  }

  return label
}

const bindDistanceAndAreaToTooltip = (layer: ExtendedLayer, toolTipText: string) => {
  layer
    .bindTooltip(toolTipText, { direction: 'bottom', permanent: !(layer instanceof Polygon) })
    .openTooltip()
}

const createPolyLayer = (drawing: PolyDrawing, line = false): PolylineType | PolygonType => {
  const polygon = L[line ? 'polyline' : 'polygon'](drawing.polygon, {
    color: line ? 'var(--green)' : 'var(--red)',
    bubblingMouseEvents: false,
  }) as PolylineType | PolygonType

  polygon.id = drawing.id

  return polygon
}

const useDrawing = (map: DrawMap) => {
  const { buildQueryString } = useBuildQueryString()
  const appInsights = useAppInsightsContext()
  const trackEvent = useTrackEvent(appInsights, 'USE_DRAW_TOOL', {})

  const [polygon] = useParam(polygonParam)
  const [polyline] = useParam(polylineParam)
  const navigate = useNavigate()
  const { panToWithPanelOffset } = useMapCenterToMarker(map)
  const { setLegendActive, setDistanceText, setDrawToolLocked } = useMapContext()

  // We needs refs here for leaflet event handlers
  const polygonRef = useRef(polygon)
  const polylineRef = useRef(polyline)
  const buildQueryStringRef = useRef(buildQueryString)
  polygonRef.current = polygon
  polylineRef.current = polyline
  buildQueryStringRef.current = buildQueryString
  const [initialDrawnItems, setInitialDrawnItems] = useState<ExtendedLayer[]>([])

  const drawnItemsGroup = useMemo(() => new L.FeatureGroup(), [])
  const [inEditMode, setInEditMode] = useState(false)
  const inCreateModeRef = useRef(false)

  /**
   * Update the state / URL with the current drawings
   * @param shape
   */
  const updateShape = (shape: { polygon: PolyDrawing | null; polyline: PolyDrawing | null }) => {
    const pathname = routing.drawTool.path
    if (shape.polygon) {
      setLegendActive(false)
      const queryString = buildQueryStringRef.current(
        [
          [polylineParam, shape.polyline],
          [polygonParam, shape.polygon],
        ],
        [pageParam],
      )
      navigate(`${pathname}?${queryString}`)
    } else {
      setDistanceText(undefined)
      const queryString = buildQueryStringRef.current(
        [[polylineParam, shape.polyline]],
        [polygonParam, pageParam],
      )
      navigate(`${pathname}?${queryString}`)
    }
  }

  /**
   * Add a tooltip showing the distance, fetch dataselection and
   * update the URL parameters
   */
  const updateDrawings = (layer: ExtendedLayer) => {
    const updatedPolygon =
      layer instanceof Polygon
        ? { id: layer.id, polygon: getLayerCoordinates(layer) }
        : polygonRef.current

    const updatedPolyline =
      layer instanceof Polygon
        ? polylineRef.current
        : { id: layer.id, polygon: getLayerCoordinates(layer) }

    setTimeout(() => {
      // Ended drawing, so be able to click on the map to search by (geo) location
      setDrawToolLocked(false)
    }, 500) // Safari hack: using this arbitrary value because in some cases leaflet is firing the mapmarker click event slightly later, causing the user to navigate to geosearch page instead of showing the polygon

    if (layer instanceof Polygon) {
      // Also pan and zoom to the drawing
      panToWithPanelOffset(layer.getBounds())
    }

    updateShape({
      polygon: updatedPolygon,
      polyline: updatedPolyline,
    })
  }

  const onDelete = useCallback((deletedLayers: ExtendedLayer[]) => {
    const deletedLayersIds = deletedLayers.map(({ id }) => id)
    const deletedLayersBounds = deletedLayers.map((layer) => {
      const coordinates = layer.getLatLngs()
      return layer instanceof Polygon
        ? (coordinates[0] as LatLngLiteral)
        : (coordinates as unknown as LatLngLiteral)
    })

    if (deletedLayersBounds.length === 0) {
      return
    }

    const newPolygon =
      polygonRef.current?.id && deletedLayersIds.includes(polygonRef.current.id)
        ? null
        : polygonRef.current

    const newPolyline =
      polylineRef.current?.id && deletedLayersIds.includes(polylineRef.current.id)
        ? null
        : polylineRef.current

    updateShape({
      polygon: newPolygon,
      polyline: newPolyline,
    })
  }, [])

  const onDrawStart = () => {
    if (isAuthenticated()) {
      trackEvent({ category: 'map' })
    }
    setDrawToolLocked(true)
  }

  const getLayersInEditMode = () => {
    const layers: ExtendedLayer[] = []

    drawnItemsGroup.eachLayer((layer) => {
      const typedLayer = layer as ExtendedLayer
      // eslint-disable-next-line no-underscore-dangle
      if (typedLayer.editing._enabled) {
        layers.push(typedLayer)
      }
    })

    return layers
  }

  const exitEditMode = () => {
    setInEditMode(false)
    getLayersInEditMode().forEach((drawnLayer) => {
      // eslint-disable-next-line no-underscore-dangle
      if (drawnLayer.editing._enabled) {
        drawnLayer.editing.disable()
      }
    })
  }

  const createPolygon = (): null | void => {
    exitEditMode()
    const drawing = new Draw.Polygon(map, {
      shapeOptions: {
        color: 'var(--red)',
        bubblingMouseEvents: false,
      },
      showLength: true,
      allowIntersection: false,
      showArea: true,
      metric: true,
      precision: {
        m: 1,
      },
      icon: drawIcon,
    }) as unknown as PolylineType

    if (onDrawStart) {
      onDrawStart()
    }

    drawing.enable()
  }

  const createPolyline = (): null | void => {
    exitEditMode()
    const drawing = new Draw.Polyline(map, {
      showLength: true,
      shapeOptions: {
        color: 'var(--green)',
        bubblingMouseEvents: false,
      },
      icon: drawIcon,
    }) as unknown as PolylineType

    if (onDrawStart) {
      onDrawStart()
    }

    drawing.enable()
  }

  const deleteDrawing = () => {
    const layersToDelete = getLayersInEditMode()
    layersToDelete.forEach((layer) => {
      drawnItemsGroup.removeLayer(layer)
    })
    if (onDelete) {
      onDelete(layersToDelete)
    }

    setInEditMode(false)
  }

  /**
   * Used to add layer to the draw map instance. This will do the following:
   * - Set a layer id
   * - Exit edit mode
   * - Add an event listener on the layer
   */
  const addLayerToMap = (layer: PolygonType | PolylineType) => {
    const handleDrawingClick = (event: LayerEvent) => {
      const { target } = event
      const { editing } = target

      setInEditMode(true)
      editing.enable()
    }

    layer.id = layer.id || uuidv4()
    exitEditMode()
    drawnItemsGroup.addLayer(layer)
    layer.on('click', handleDrawingClick)
  }

  useEffect(() => {
    const onEditVertex = (e: L.DrawEvents.EditVertex) => {
      setDrawToolLocked(true)
      updateDrawings(e.poly as ExtendedLayer)
    }

    map.on(L.Draw.Event.EDITVERTEX as any, onEditVertex as any)

    // Clean up
    return () => {
      drawnItemsGroup.clearLayers()
      map.off(L.Draw.Event.EDITVERTEX as any, onEditVertex as any)
    }
  }, [])

  useEffect(() => {
    if (!polygon) {
      setDistanceText(undefined)
    }
  }, [polygon])

  /**
   * This effect will handle loading drawings from initial state and
   * handle adding and removing drawings when navigating
   */
  useEffect(() => {
    // Delete old drawings / layers
    drawnItemsGroup.eachLayer((layer) => {
      const typedLayer = layer as ExtendedLayer
      drawnItemsGroup.removeLayer(typedLayer)
    })

    // Add layers if they don't exist on the map
    const drawnItems: Array<PolylineType | PolygonType> = []
    if (polygon) {
      try {
        drawnItems.push(createPolyLayer(polygon))
      } catch (error) {
        console.error(error)
      }
    }
    if (polyline) {
      try {
        drawnItems.push(createPolyLayer(polyline, true))
      } catch (error) {
        console.error(error)
      }
    }
    if (drawnItems.length) {
      setInitialDrawnItems(drawnItems)
    }
  }, [polygon, polyline])

  useEffect(() => {
    const onDrawCreated = (e: DrawEvents.Created) => {
      addLayerToMap(e.layer as unknown as ExtendedLayer)

      updateDrawings(e.layer as unknown as ExtendedLayer)
    }

    // End editing mode by clicking outside the map
    const handleClick = () => {
      if (!inCreateModeRef.current) {
        exitEditMode()
      }
    }

    const handleKeyDown = (e: LeafletKeyboardEvent) => {
      switch (e.originalEvent.key) {
        case 'Delete':
        case 'Backspace':
          deleteDrawing()
          break

        case 'Enter':
        case 'Escape':
          exitEditMode()
          break

        default:
      }
    }

    map.on(Draw.Event.CREATED, onDrawCreated as LeafletEventHandlerFn)
    map.on('click', handleClick)
    map.on('keydown', handleKeyDown)
    map.addLayer(drawnItemsGroup)

    return () => {
      map.off(Draw.Event.CREATED, onDrawCreated as LeafletEventHandlerFn)
      map.off('click', handleClick)
      map.off('keydown', handleKeyDown)
      if (map.hasLayer(drawnItemsGroup)) {
        map.removeLayer(drawnItemsGroup)
      }
    }
  }, [])

  useEffect(() => {
    if (initialDrawnItems && initialDrawnItems.length) {
      initialDrawnItems.forEach((drawnItem) => {
        addLayerToMap(drawnItem)

        const distanceText = getDistanceLabel(drawnItem)
        setDistanceText(distanceText)
        bindDistanceAndAreaToTooltip(drawnItem, distanceText)

        const element = drawnItem.getElement()
        if (element) {
          L.DomUtil.addClass(element as HTMLElement, DRAWN_ITEM_CLASS)
        }
      })
    }
  }, [initialDrawnItems])

  return { createPolyline, createPolygon, inEditMode, deleteDrawing }
}

export default useDrawing
